/* eslint-disable no-console */
import Vue from 'vue'
import Vuex from 'vuex'
import Router from 'vue-router'
import vuetify from './plugins/vuetify'
import axios from 'axios'
import moment from 'moment'
import {marked} from 'marked'
import EventBus from './eventbus'
import routes from './router'
import { beforeEach } from './router'
import filters from './filters'

import App from './App.vue'

import CrmPlugin from './plugins/crm'
import CrmCommonPlugin from '@gr8crm/vue-gr8crm-common'
import CrmBillingPlugin from 'vue-gr8crm-billing'
import CrmSettingsPlugin from 'vue-gr8crm-settings'
import CrmContactPlugin from '@gr8crm/vue-gr8crm-contact'
import CrmContentPlugin from '@gr8crm/vue-gr8crm-content'
import Notifications from 'vue-gr8crm-notification'
import CrmActivityPlugin from 'vue-gr8crm-activity'

import AvtalaContractPlugin from './plugin'
import rootStore from './store'
import accountStore from './store/account'
import sequenceStore from './store/sequence'
import commentStore from './store/comment'
import wfStore from './store/workflow'
import scheduleStore from './store/schedule'
import auditStore from './store/audit'
import policyStore from './store/policy'
import followUpStore from './store/followup'
import signStore from './store/sign'
import emailStore from './store/email'
import invoiceStore from './store/invoice'
import fortnoxStore from './store/fortnox'
import migrationStore from './store/migration'

import '@/components/base'

import '@gr8crm/vue-gr8crm-content/dist/vue-gr8crm-content.css'
import '@gr8crm/vue-gr8crm-contact/dist/vue-gr8crm-contact.css'

Vue.config.productionTip = false

moment.locale('sv')

const renderer = new marked.Renderer()
renderer.link = function(href, title, text) {
  var link = marked.Renderer.prototype.link.apply(this, arguments)
  return link.replace("<a","<a target='_blank'")
}

marked.setOptions({ renderer: renderer })

Vue.use(Router)
Vue.use(Vuex)

const errorHandler = (message, error) => EventBus.$emit('error', { message, error })

axios.defaults.baseURL = process.env.VUE_APP_API
axios.defaults.withCredentials = true

axios.interceptors.request.use(config => {
  config.headers['X-CRM-Client'] = process.env.VUE_APP_CLIENT
  config.headers['Cache-Control'] = 'no-cache'
  config.maxContentLength = 1024 * 1024 * 32
  config.maxBodyLength = 1024 * 1024 * 30
  return config
})
axios.interceptors.response.use(response => {
  return response
}, error => {
  if (error.config?.method === 'head') {
    return Promise.reject(error);
  }
  if (error.response) {
    const response = error.response
    const headers = response.headers || {}
    if (response.status === 401) {
      errorHandler('error.unauthorized', error)
    } else if (response.status === 403) {
      errorHandler('error.forbidden', 'Du saknar behörighet att utföra operationen')
    } else if (headers['content-type'] === 'application/problem+json') {
      const problem = response.data || { title: 'Error', detail: error }
      const title = problem.title || 'Error'
      const message = problem.detail || error
      errorHandler(title, message)
    } else {
      errorHandler("error.default", error);
    }
  } else if(error.request) {
    errorHandler('error.request', error)
  } else {
    errorHandler('error.default', error)
  }
  return Promise.reject(error)
})

Vue.prototype.$http = axios
Vue.prototype.$eventBus = EventBus

Vue.filter('formatTimestamp', filters.formatDateTime)
Vue.filter('formatDate', filters.formatDate)
Vue.filter('formatDateShort', function (value) {
  if (value) {
    return moment(value).locale('sv').format('D MMM YYYY')
  }
})

Vue.filter('formatDateTime', filters.formatDateTime)
Vue.filter('formatDateTimeLong', function (value) {
  if (value) {
    return moment(value).locale('sv').format('ddd D MMM [kl] HH:mm')
  }
})
Vue.filter('formatTime', function (value) {
  if (value) {
    return moment(value).locale('sv').format('H:mm')
  }
})
const moneyFormatter = new Intl.NumberFormat("sv-SE", {
  style: "currency",
  currency: "SEK",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  trailingZeroDisplay: "stripIfInteger",
  useGrouping: "auto",
});
Vue.filter('formatMoney', function (value) {
  return moneyFormatter.format(value || 0)
})
const currencyFormatter = function(currency = 'SEK', locale = 'sv-SE') {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    trailingZeroDisplay: "stripIfInteger",
    useGrouping: "auto",
  });
}
Vue.filter(
  "formatCurrency",
  function (value, currency = "SEK", locale = "sv-SE") {
    return value != null ? currencyFormatter(currency, locale).format(value || 0) : "";
  }
);
Vue.filter('formatBytes', function (a) {
  if (a === 0) return '0'
  var c = 1024
  var e = ['', 'KB', 'MB', 'GB', 'TB']
  var f = Math.floor(Math.log(a) / Math.log(c))
  return parseFloat((a / Math.pow(c, f)).toFixed(0)) + ' ' + e[f]
})
Vue.filter('fullAddress', filters.fullAddress)

const store = new Vuex.Store({
  namespaced: true,
  strict: process.env.NODE_ENV !== 'production',
  ...rootStore
})

store.registerModule('account', accountStore)
store.registerModule('sequence', sequenceStore)
store.registerModule('comment', commentStore)
store.registerModule('workflow', wfStore)
store.registerModule('schedule', scheduleStore)
store.registerModule('audit', auditStore)
store.registerModule('policy', policyStore)
store.registerModule('followup', followUpStore)
store.registerModule('signing', signStore)
store.registerModule('email', emailStore)
store.registerModule('invoice', invoiceStore)
store.registerModule('fortnox', fortnoxStore)
store.registerModule('migration', migrationStore)

// Workaround because Vuew Router does not have a getAllRoutes() method.
const routeCollector = {
  _routes: [],
  addRoutes: function (array) {
    for(let i = 0; i < array.length; i++) {
      this._routes.push(array[i])
    }
  },
  getAllRoutes: function() {
    return this._routes
  }
}

routeCollector.addRoutes(routes)

const uiPlugins = [
  {
    component: () => import(/* webpackChunkName: "plugin" */ '@/components/contact/ContactAttrPlugin.vue'),
    location: 'contact-show'
  },
  {
    component: () => import(/* webpackChunkName: "plugin" */ '@/components/contact/ContractListPlugin.vue'),
    location: 'contact-show'
  },
]
Vue.use(CrmCommonPlugin, { store, router: routeCollector, plugins: uiPlugins })
Vue.use(CrmPlugin, { store })
Vue.use(CrmBillingPlugin, { store, router: routeCollector })
Vue.use(CrmSettingsPlugin, { store, router: routeCollector })
Vue.use(CrmContentPlugin, { store, router: routeCollector })
Vue.use(CrmContactPlugin, { store, router: routeCollector })
Vue.use(CrmActivityPlugin, { store, router: routeCollector })
Vue.use(AvtalaContractPlugin, { store, router: routeCollector })

const scrollBehavior = (to, from, savedPosition) => {
  return savedPosition || { x: 0, y: 0 }
}

const router = new Router({
  mode: 'history',
  scrollBehavior,
  base: process.env.VUE_APP_BASE,
  routes: routeCollector.getAllRoutes()
})

router.beforeEach(beforeEach(store))

export default async function (keycloak) {
  Vue.prototype.$keycloak = keycloak
  EventBus.$on('application:init', (data) => {
    EventBus.$disconnect()
    const tenant = data.tenant
    const sub = data.sub
    const session = data.session
    if (tenant && sub && session) {
      EventBus.$connect(process.env.VUE_APP_WS + '/notifications/' + encodeURIComponent(tenant.id) + '/' + encodeURIComponent(sub)  + '/' + encodeURIComponent(session))
      store.dispatch('fetchNotifications')
    }
  })

  EventBus.$on('change-password', () => {
    const appUrl = process.env.VUE_APP_API + process.env.VUE_APP_BASE
    keycloak.accountManagement({ redirectUri: appUrl })
  })

  let error = null
  try {
    await keycloak.loadUserInfo()
      .then(userInfo => {
        store.commit('account/setUser', userInfo)
        Vue.use(Notifications, {
          store,
          url: process.env.VUE_APP_WS + '/notifications/' + encodeURIComponent(userInfo.sub),
          connectManually: true
        })
      })
  } catch (err) {
    console.error(err)
    error = err
  }

  if (error) {
    return new Vue({
      router,
      store,
      vuetify,
      render: h => h(Error, { props: { error }})
    }).$mount('#app')
  }

  return new Vue({
    router,
    store,
    vuetify,
    render: h => h(App)
  }).$mount('#app')
}
